import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { getTranslatedJobItemTitle } from '../profile-comparison/build-profile-comparison';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import type { ComparedItem } from './use-role-comparison-data';

export const RoleComparisonHeaderCell: FunctionComponent<{
    readonly comparedItem: ComparedItem;
    readonly canRemove: boolean;
    readonly itemType: 'job-level' | 'job-role';
    readonly onOpen: (itemId: string) => void;
    readonly onRemove: (itemId: string) => void;
    readonly onSelectVersion: (itemId: string, version: string) => void;
}> = ({ canRemove, comparedItem, itemType, onOpen, onRemove, onSelectVersion }) => {
    const translate = useTranslate();
    const removeLabelKey =
        itemType === 'job-role'
            ? 'pages.job-architecture-v2.job-profile-role-comparison-remove-role'
            : 'pages.job-architecture-v2.job-profile-level-comparison-remove-level';
    const { activeLanguage } = useActiveLanguage();
    const { details, error, id, isFetching, parentTitle, versions } = comparedItem;
    const { formattedVersions } = useFormattedVersions(versions ?? []);

    if (is.undefined(details) || is.undefined(versions)) {
        if (is.null(error)) {
            return (
                <div
                    className="flex min-w-0 w-full flex-col gap-s py-s"
                    data-testid={`comparison-role-header-${id}`}
                    aria-busy={true}
                >
                    <SkeletonBase className="h-l w-3/4" />
                    <SkeletonBase className="h-xl w-1/2" />
                </div>
            );
        }

        return (
            <div
                className="flex min-w-0 w-full items-center justify-between gap-xs py-s text-body-xs text-danger"
                data-testid={`comparison-role-header-${id}`}
                role="alert"
                title={error.message}
            >
                {translate('pages.job-architecture-v2.job-profile-error-prefix')}
                {canRemove ? (
                    <Button
                        aria-label={translate(removeLabelKey, {
                            name: id,
                        })}
                        iconOnly={true}
                        size="sm"
                        variant="tertiary"
                        onClick={() => onRemove(id)}
                    >
                        <IconCrossSmall size={16} />
                    </Button>
                ) : null}
            </div>
        );
    }

    const title = getTranslatedJobItemTitle(details, activeLanguage);
    const selectedVersionId = details.versionInfo?.version.toString();
    const currentVersion = formattedVersions.find((version) => version.id === selectedVersionId);

    return (
        <div
            className="flex min-w-0 w-full flex-col items-start gap-xxs py-xxs"
            data-testid={`comparison-role-header-${id}`}
            aria-busy={isFetching}
        >
            <div className="flex min-w-0 w-full items-start justify-between gap-xs">
                <Button className="min-h-10 !justify-start !px-0 text-left" variant="link" onClick={() => onOpen(id)}>
                    <span className="line-clamp-2 text-left text-wrap-balance">{title}</span>
                </Button>
                {canRemove ? (
                    <Button
                        aria-label={translate(removeLabelKey, {
                            name: title,
                        })}
                        className="shrink-0"
                        iconOnly={true}
                        size="sm"
                        variant="tertiary"
                        onClick={() => onRemove(id)}
                    >
                        <IconCrossSmall size={16} />
                    </Button>
                ) : null}
            </div>
            {is.nonEmptyString(parentTitle) ? (
                <span className="line-clamp-1 text-body-xxs font-normal text-text-muted">{parentTitle}</span>
            ) : null}
            <div className="flex min-w-0 w-full items-center justify-between gap-s">
                <div className="min-w-0 w-fit max-w-[14rem] shrink tabular-nums">
                    <VersionSelect
                        ariaLabel={translate('pages.job-architecture-v2.job-profile-version-for', { name: title })}
                        contentInset="none"
                        currentVersion={currentVersion}
                        disabled={versions.length < 2}
                        placeholder={
                            !is.undefined(selectedVersionId)
                                ? translate('pages.job-architecture-v2.job-profile-version', {
                                      version: selectedVersionId,
                                  })
                                : ''
                        }
                        versions={formattedVersions}
                        onSwitchVersion={(version) => onSelectVersion(id, version)}
                    />
                </div>
                <div className="shrink-0">
                    {isFetching ? <Spinner className="mr-0 text-text-primary" /> : currentVersion?.badge}
                </div>
            </div>
        </div>
    );
};
